import React from 'react';
import { FestivalData, SHOW_ITEMS_LAYOUT } from '../../../../../model/FestivalData';
import { useMessages } from '../../../../../i18n/I18n';
import { cssColor } from '../../../../../utils/colors';
import './ShowListLayoutPicker.css';

type Stylesheet = FestivalData['infos']['stylesheet'];

interface ShowListLayoutPickerProps {
  stylesheet: Stylesheet;
  value: SHOW_ITEMS_LAYOUT;
  onChange: (layout: SHOW_ITEMS_LAYOUT) => void;
  getApiImageUrl: (identifier: string) => string;
}

// Their titles and descriptions are in t.showList.layouts
const LAYOUTS: SHOW_ITEMS_LAYOUT[] = ['card', 'row'];

// The previews draw the app's show list at phone size (dp = px), then scale it down
const SCREEN_WIDTH = 390;
const SCREEN_HEIGHT = 600;
const SCALE = 0.68;

const SAMPLE_DAY = 'Jeu 25';
const SAMPLE_SHOWS = [
  { artist: 'Lumen Drift', style: 'Electro', scene: 'Main Stage', start: '19:50', end: '20:50', duration: '1h', subscribed: true, colors: ['#ff7a59', '#7b2ff7'] },
  { artist: 'Mira Solène', style: 'Folk', scene: 'Chapiteau', start: '21:00', end: '22:15', duration: '1h15', subscribed: false, colors: ['#11998e', '#5ad1a4'] },
  { artist: 'Les Échos Sauvages', style: 'Rock', scene: 'Open Air', start: '22:30', end: '23:45', duration: '1h15', subscribed: false, colors: ['#f7971e', '#d4145a'] },
  { artist: 'Kollektiv Nachtfalter & Friends', style: 'Techno', scene: 'Main Stage', start: '00:00', end: '01:30', duration: '1h30', subscribed: false, colors: ['#4568dc', '#b06ab3'] },
  { artist: 'Oona', style: 'Dub', scene: 'Chapiteau', start: '01:30', end: '02:30', duration: '1h', subscribed: true, colors: ['#e65c00', '#f9d423'] },
];
type SampleShow = typeof SAMPLE_SHOWS[number];

const samplePhoto = ([from, to]: string[]) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>`
    + `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='${from}'/><stop offset='1' stop-color='${to}'/></linearGradient></defs>`
    + `<rect width='100' height='100' fill='url(#g)'/>`
    + `<circle cx='50' cy='40' r='15' fill='white' fill-opacity='0.45'/>`
    + `<path d='M20 100c0-19 13-33 30-33s30 14 30 33z' fill='white' fill-opacity='0.45'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const HEART_OUTLINE = 'M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z';
const HEART_FILLED = 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';
const LOCATION = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
const CALENDAR = 'M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z';

const Icon: React.FC<{ path: string; size: number; color: string }> = ({ path, size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"><path d={path} fill={color} /></svg>
);

// The gradient under the text of a photo, down to the overlay color at its opacity
const imageOverlay = (stylesheet: Stylesheet) => {
  const color = cssColor(stylesheet.image_overlay_color, '#000000');
  const opacity = Math.min(1, Math.max(0, stylesheet.image_overlay_opacity ?? 0.7));
  return `linear-gradient(to bottom, transparent 15%, color-mix(in srgb, ${color} ${opacity * 100}%, transparent))`;
};

const CardItem: React.FC<{ show: SampleShow; stylesheet: Stylesheet }> = ({ show, stylesheet }) => (
  <div
    className="slp-card"
    style={{ height: stylesheet.show.cell_height, backgroundImage: samplePhoto(show.colors), borderRadius: stylesheet.corner_radius ?? 8 }}
  >
    <div className="slp-card-shade" style={{ background: imageOverlay(stylesheet) }} />
    <div className="slp-card-content">
      <div className="slp-card-top">
        <span className="slp-card-chip">{show.style.toUpperCase()}</span>
        <span className="slp-card-favorite">
          <Icon path={show.subscribed ? HEART_FILLED : HEART_OUTLINE} size={20} color="#000000" />
        </span>
      </div>
      <div className="slp-card-bottom">
        <div className="slp-card-name" style={{ color: cssColor(stylesheet.show.items_artist_name_color, '#000000') }}>
          {show.artist}
        </div>
        <div className="slp-card-line slp-card-scene">
          <Icon path={LOCATION} size={16} color="#ffffff" />
          <span style={{ color: cssColor(stylesheet.show.items_scene_name_color, '#000000') }}>{show.scene}</span>
        </div>
        <div className="slp-card-line slp-card-date">
          <Icon path={CALENDAR} size={16} color={cssColor(stylesheet.show.items_show_date_color, '#000000')} />
          <span style={{ color: cssColor(stylesheet.show.items_show_date_color, '#000000') }}>
            25-06-2026 à {show.start} - {show.end} ({show.duration})
          </span>
        </div>
      </div>
    </div>
  </div>
);

const RowItem: React.FC<{ show: SampleShow; stylesheet: Stylesheet }> = ({ show, stylesheet }) => {
  const tag = {
    background: cssColor(stylesheet.show.items_tag_background_color, '#FFFFFF'),
    color: cssColor(stylesheet.show.items_tag_text_color, '#000000'),
  };
  return (
    <div className="slp-row">
      <div
        className="slp-row-photo"
        style={{
          backgroundImage: samplePhoto(show.colors),
          backgroundColor: `color-mix(in srgb, ${tag.background} 15%, transparent)`,
          borderRadius: stylesheet.corner_radius ?? 8,
        }}
      />
      <div className="slp-row-body">
        <div className="slp-row-name" style={{ color: cssColor(stylesheet.show.items_artist_name_color, '#000000') }}>
          {show.artist.toUpperCase()}
        </div>
        <span className="slp-row-tag" style={tag}>{show.start} - {show.end} · {show.scene}</span>
        <span className="slp-row-tag" style={tag}>{show.style}</span>
      </div>
      <span className="slp-row-favorite" style={{ background: tag.background }}>
        <Icon path={show.subscribed ? HEART_FILLED : HEART_OUTLINE} size={24} color={tag.color} />
      </span>
    </div>
  );
};

const PhonePreview: React.FC<{ layout: SHOW_ITEMS_LAYOUT; stylesheet: Stylesheet; getApiImageUrl: (identifier: string) => string }> = ({
  layout, stylesheet, getApiImageUrl,
}) => {
  const background = stylesheet.background;
  const primary = cssColor(background.primary_color, '#ffffff');
  const image = background.type === 'Image' ? background.image : null;
  const imageUrl = image?.type === 'ExternalLink' ? image.url
    : image?.type === 'ApiImage' && !image.file ? getApiImageUrl(image.identifier)
    : null;
  const screenBackground = background.type === 'Gradient'
    ? `linear-gradient(${primary}, ${cssColor(background.secondary_color, '#ffffff')})`
    : background.type === 'Image' ? cssColor(background.base_color, '#ffffff') : primary;
  const titleColor = cssColor(stylesheet.show.title_color, '#000000');

  return (
    <div className="slp-phone" style={{ width: SCREEN_WIDTH * SCALE, height: SCREEN_HEIGHT * SCALE }}>
      <div
        className="slp-screen"
        style={{ width: SCREEN_WIDTH, height: SCREEN_HEIGHT, transform: `scale(${SCALE})`, background: screenBackground }}
      >
        {imageUrl && (
          <div
            className="slp-screen-image"
            style={{ backgroundImage: `url("${imageUrl}")`, opacity: background.image_opacity ?? 1 }}
          />
        )}
        <div className="slp-list">
          <div className="slp-day">
            <span className="slp-day-line" style={{ background: titleColor }} />
            <span className="slp-day-name" style={{ color: titleColor }}>{SAMPLE_DAY}</span>
            <span className="slp-day-line" style={{ background: titleColor }} />
          </div>
          {SAMPLE_SHOWS.map((show) => layout === 'card'
            ? <CardItem key={show.artist} show={show} stylesheet={stylesheet} />
            : <RowItem key={show.artist} show={show} stylesheet={stylesheet} />)}
        </div>
      </div>
    </div>
  );
};

const ShowListLayoutPicker: React.FC<ShowListLayoutPickerProps> = ({ stylesheet, value, onChange, getApiImageUrl }) => {
  const t = useMessages('infos');
  return (
    <div className="slp-options" role="radiogroup" aria-label={t.showList.title}>
      {LAYOUTS.map((layout) => (
        <label key={layout} className={`slp-option${value === layout ? ' selected' : ''}`}>
          <div className="slp-option-header">
            <input
              type="radio"
              name="show_items_layout"
              value={layout}
              checked={value === layout}
              onChange={() => onChange(layout)}
            />
            <div>
              <span className="slp-option-title">{t.showList.layouts[layout].title}</span>
              <span className="slp-option-description">{t.showList.layouts[layout].description}</span>
            </div>
          </div>
          <PhonePreview layout={layout} stylesheet={stylesheet} getApiImageUrl={getApiImageUrl} />
        </label>
      ))}
    </div>
  );
};

export default ShowListLayoutPicker;
